/**
 * Technische Anlagen — pure render helpers (HTML strings) for the property-level section built
 * by `technicalAssetsSection.js`: shared atoms, the asset rows and their summary. The
 * Energieausweis rows live in `technicalAssetsSection.energyCertificates.js`, styles and the
 * table/sub-section chrome in `technicalAssetsSection.layout.js`. No fetches live here. All
 * cross-file references are inside function bodies, so gulp concat order does not matter.
 *
 * Row data comes from `GET /api/technical-assets?propertyId=…&include=display`: each asset
 * carries `documents[]`, and the response adds `highlightsByAssetId` (type-field values from
 * the Lux kind catalog, selects already labelled) and `contactNames` (id → display name).
 */
function isoDateToGermanDisplay(iso) {
  if (!iso) return '—'
  const slice = String(iso).slice(0, 10)
  const m = slice.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!m) return escapeHtml(slice)
  return `${m[3]}.${m[2]}.${m[1]}`
}

/** `2026-10-15` → Date at local midnight, or null. */
function isoDayToLocalDate(iso) {
  const m = String(iso || '')
    .slice(0, 10)
    .match(/^(\d{4})-(\d{2})-(\d{2})$/)
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null
}

function technicalAssetDaysUntil(iso) {
  const d = isoDayToLocalDate(iso)
  if (!d) return null
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return Math.round((d - today) / 86400000)
}

/**
 * de-DE number; `fractionDigits` fixes the decimals, omit it for "as many as needed" (max 2).
 * (Not `formatGermanNumber` — that global in utils/ parses German *strings* and is bundled too.)
 */
function taFormatNumber(value, fractionDigits) {
  const n = Number(value)
  if (!Number.isFinite(n)) return '—'
  return n.toLocaleString('de-DE', {
    minimumFractionDigits: fractionDigits ?? 0,
    maximumFractionDigits: fractionDigits ?? 2,
  })
}

/**
 * Property-scoped Lux pages (the building is a field on the asset). On hosted
 * environments the reverse proxy must forward `/properties/{hrId}/technical-assets…` to Lux.
 */
function luxTechnicalAssetDetailUrl(propertyHrId, assetId) {
  const p = encodeURIComponent(String(propertyHrId))
  const id = encodeURIComponent(String(assetId))
  return `${baseApiUrl}/properties/${p}/technical-assets/${id}`
}

/** @param {string} [typ] preselects the kind on the Lux create page (`?typ=`). */
function luxTechnicalAssetCreateUrl(propertyHrId, typ) {
  const p = encodeURIComponent(String(propertyHrId))
  const query = typ ? `?typ=${encodeURIComponent(String(typ))}` : ''
  return `${baseApiUrl}/properties/${p}/technical-assets/create${query}`
}

const TECHNICAL_ASSET_ROW_ICONS = {
  edit: '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/><path d="m15 5 4 4"/></svg>',
  delete:
    '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>',
  building:
    '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4" y="2" width="16" height="20" rx="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01M16 6h.01M12 6h.01M12 10h.01M12 14h.01M16 10h.01M16 14h.01M8 10h.01M8 14h.01"/></svg>',
  file: '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>',
  check:
    '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></svg>',
}

/** Dim the row and swap the clicked icon for a spinner while a row action runs. */
function setTechnicalAssetRowBusy(row, activeButton) {
  row.classList.add('impowerx-ta-row-busy')
  row.querySelectorAll('.impowerx-ta-icon-btn').forEach((btn) => {
    btn.disabled = true
  })
  if (activeButton) {
    activeButton.innerHTML = '<span class="impowerx-ta-spinner"></span>'
  }
}

/**
 * Undo `setTechnicalAssetRowBusy` when the row stays on screen (the refetch after
 * the action failed). A lingering busy row would block the tab-focus refreshes.
 */
function clearTechnicalAssetRowBusy(row, activeButton, iconKey) {
  row.classList.remove('impowerx-ta-row-busy')
  row.querySelectorAll('.impowerx-ta-icon-btn').forEach((btn) => {
    btn.disabled = false
  })
  if (activeButton && TECHNICAL_ASSET_ROW_ICONS[iconKey]) {
    activeButton.innerHTML = TECHNICAL_ASSET_ROW_ICONS[iconKey]
  }
}

// ---------------------------------------------------------------------------
// Small HTML atoms (all return escaped HTML)
// ---------------------------------------------------------------------------

function taPill(text, tone) {
  return `<span class="impowerx-ta-pill impowerx-ta-pill--${tone}">${escapeHtml(text)}</span>`
}

function taTag(text) {
  return `<span class="impowerx-ta-tag">${escapeHtml(text)}</span>`
}

function taLine(icon, html) {
  return `<div class="impowerx-ta-line">${TECHNICAL_ASSET_ROW_ICONS[icon] || ''}<span>${html}</span></div>`
}

function taSecondary(html) {
  return html ? `<div class="impowerx-ta-secondary">${html}</div>` : ''
}

/** Resolved contact name or a neutral fallback when the lookup did not return the id. */
function taContactName(contactNames, id) {
  return (contactNames && contactNames[String(id)]) || `Kontakt #${id}`
}

function taContactNames(contactNames, ids) {
  return (ids || []).map((id) => taContactName(contactNames, id))
}

/** Highlight value → display string (contacts resolved, booleans → Ja/Nein, dates → dd.mm.yyyy). */
function taHighlightValue(h, contactNames) {
  switch (h.input) {
    case 'boolean':
      return h.value ? 'Ja' : 'Nein'
    case 'date':
      return isoDateToGermanDisplay(h.value)
    case 'contact':
      return taContactName(contactNames, h.value)
    case 'contactMulti':
      return taContactNames(contactNames, h.value).join(', ')
    case 'float':
      return taFormatNumber(h.value)
    default:
      return String(h.value)
  }
}

// ---------------------------------------------------------------------------
// Technische Anlagen rows
// ---------------------------------------------------------------------------

const TECHNICAL_ASSET_CYCLE_UNITS = {
  week: { one: 'jede Woche', plural: 'Wochen' },
  month: { one: 'jeden Monat', plural: 'Monate' },
  year: { one: 'jedes Jahr', plural: 'Jahre' },
}

function highlightValue(highlights, key) {
  const found = (highlights || []).find((h) => h && h.key === key)
  return found ? found.value : null
}

/** Column, then a type-field highlight, then a value still stored in type_fields. */
function taContractValue(asset, highlights, key) {
  if (asset && asset[key] != null && asset[key] !== '') return asset[key]
  const fromHighlight = highlightValue(highlights, key)
  if (fromHighlight != null && fromHighlight !== '') return fromHighlight
  const fields = asset && asset.typeFields
  if (fields && fields[key] != null && fields[key] !== '') return fields[key]
  return null
}

function taPlanCycleLabel(plan) {
  const unit = TECHNICAL_ASSET_CYCLE_UNITS[(plan && plan.recurrenceKind) || '']
  const count = Number(plan && plan.intervalCount)
  if (!unit || !Number.isInteger(count) || count < 1) return ''
  return count === 1 ? unit.one : `alle ${count} ${unit.plural}`
}

/** Per-plan contracts override the legacy shared contract. */
function taPlanIsActive(plan, asset) {
  const details = plan && plan.details
  if (details) {
    return !['stopped', 'cancelled'].includes(details.contractStatus)
  }
  return !['stopped', 'cancelled'].includes(asset.contractStatus)
}

/** Earliest upcoming (or most overdue) maintenance plan with a date, or null. */
function taNextMaintenancePlan(asset) {
  const plans = (asset.maintenancePlans || []).filter(
    (p) => p && p.nextMaintenanceDate && taPlanIsActive(p, asset)
  )
  if (!plans.length) return null
  return plans
    .slice()
    .sort((a, b) =>
      String(a.nextMaintenanceDate).localeCompare(String(b.nextMaintenanceDate))
    )[0]
}

/** Status pill from the next maintenance date: overdue → danger, due within 30 days → warn, else ok. */
function taMaintenanceStatusPill(asset) {
  const plans = asset.maintenancePlans || []
  if (plans.length && plans.every((plan) => !taPlanIsActive(plan, asset))) {
    return taPill('Wartungen pausiert', 'warn')
  }
  if (!plans.length && asset.contractStatus === 'stopped') {
    return taPill('Gestoppt', 'warn')
  }
  if (!plans.length && asset.contractStatus === 'cancelled') {
    return taPill('Gekündigt', 'light')
  }
  const plan = taNextMaintenancePlan(asset)
  if (!plan) return taPill('Aktiv', 'ok')
  const days = technicalAssetDaysUntil(plan.nextMaintenanceDate)
  if (days != null && days < 0) return taPill('Wartung überfällig', 'danger')
  if (days != null && days <= 30) return taPill('Wartung fällig', 'warn')
  return taPill('Aktiv', 'ok')
}

/** Highlight keys that already have a dedicated place in another column (company / fristen / gebäude). */
const TECHNICAL_ASSET_HIGHLIGHTS_SHOWN_ELSEWHERE =
  /^(maintenanceInterval|noticePeriodMonths|contractStartDate|contractEndDate)$|linkedBuildingHrId$/i

function renderTechnicalAssetNameCell(a, ctx) {
  const href = ctx.detailHref(a)
  const highlights =
    (ctx.highlightsByAssetId && ctx.highlightsByAssetId[String(a.id)]) || []
  const chips = highlights
    .filter((h) =>
      ['select', 'boolean', 'text', 'integer', 'float'].includes(h.input)
    )
    .filter((h) => !TECHNICAL_ASSET_HIGHLIGHTS_SHOWN_ELSEWHERE.test(h.key))
    .slice(0, 4)
    .map(
      (h) =>
        `<span>${escapeHtml(h.label)}: <b>${escapeHtml(taHighlightValue(h, ctx.contactNames))}</b></span>`
    )
  const sub = [
    a.manufacturerContactId != null
      ? `Hersteller: ${escapeHtml(taContactName(ctx.contactNames, a.manufacturerContactId))}`
      : '',
    a.installationDate
      ? `Einbau ${isoDateToGermanDisplay(a.installationDate)}`
      : '',
  ]
    .filter(Boolean)
    .join(' · ')
  return `
    <div class="impowerx-ta-titleline">
      <span class="impowerx-ta-primary"><a href="${escapeHtml(href)}">${escapeHtml(a.name || '—')}</a></span>
      ${taTag(`TA-${a.id}`)}
    </div>
    <div class="impowerx-ta-titleline" style="margin-top:4px">${taPill(a.typ || '—', 'light')} ${taMaintenanceStatusPill(a)}</div>
    ${taSecondary(sub)}
    ${chips.length ? `<div class="impowerx-ta-dots">${chips.join('')}</div>` : ''}`
}

/**
 * Kinds with no physical installation. Keep in step with `kindIsPhysical`
 * in src/lib/technical-assets/kind-config.ts.
 */
const TECHNICAL_ASSET_NON_PHYSICAL_KINDS = {
  Energieausweis: true,
  Legionellenbeprobung: true,
}

/** Stored linked-building HR ids → building names of the property (HR id when unknown). */
function taServedBuildingLabels(value, buildings) {
  return String(value || '')
    .split(/[,;\n]/)
    .map((part) => part.trim())
    .filter(Boolean)
    .map((hrId) => {
      const building = (buildings || []).find(
        (b) => b && b.buildingHrId === hrId
      )
      return (building && building.name) || hrId
    })
}

function renderTechnicalAssetBuildingCell(a, ctx) {
  const highlights =
    (ctx.highlightsByAssetId && ctx.highlightsByAssetId[String(a.id)]) || []
  const tags = highlights
    .filter((h) => /linkedBuildingHrId$/i.test(h.key) && h.value)
    .flatMap((h) =>
      h.input === 'buildingLinks'
        ? taServedBuildingLabels(h.value, ctx.buildings).map((label) =>
            taTag(
              `${TECHNICAL_ASSET_NON_PHYSICAL_KINDS[a.typ] ? 'Zugeordnet' : 'Mitversorgt'}: ${label}`
            )
          )
        : [taTag(`${h.label}: ${h.value}`)]
    )
  return `
    ${taLine('building', `<span class="impowerx-ta-strong">${escapeHtml(a.buildingLabel || '—')}</span>`)}
    ${a.buildingHrId && a.buildingHrId !== a.buildingLabel ? taSecondary(escapeHtml(a.buildingHrId)) : ''}
    ${tags.length ? `<div class="impowerx-ta-tags">${tags.join('')}</div>` : ''}`
}

/** Avatars shown before the "+N" button; same as the Task-Automator recipients column. */
const TA_AVATAR_LIMIT = 2

/** Task-Automator initials: one word → first two letters, else first + last word. */
function taInitials(name) {
  const parts = String(name || '')
    .split(/\s+/)
    .filter(Boolean)
  if (!parts.length) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
}

/** Overlapping initials; the "+N" button reveals the hidden ones (see `wireTechnicalAssetsRowActions`). */
function taAvatarStack(names) {
  const rest = Math.max(0, names.length - TA_AVATAR_LIMIT)
  const avatars = names
    .map(
      (name, i) =>
        `<span class="impowerx-ta-avatar${i >= TA_AVATAR_LIMIT ? ' impowerx-ta-avatar--extra' : ''}" title="${escapeHtml(name)}">${escapeHtml(taInitials(name))}</span>`
    )
    .join('')
  const more = rest
    ? `<button type="button" class="impowerx-ta-avatar impowerx-ta-avatar-more" title="Alle Sachbearbeiter anzeigen">+${rest}</button>`
    : ''
  return `<div class="impowerx-ta-avatar-stack">${avatars}${more}</div>`
}

/** Company line ("+N Partner" for further firms) and the Sachbearbeiter avatar stack. */
function renderTechnicalAssetCompanyCell(a, ctx) {
  const highlights =
    (ctx.highlightsByAssetId && ctx.highlightsByAssetId[String(a.id)]) || []
  const planCompanyIds = (a.maintenancePlans || []).flatMap((plan) =>
    plan.details
      ? [
          plan.details.assignedMaintenanceCompanyContactId,
          ...(plan.details.additionalMaintenanceCompanyContactIds || []),
        ].filter(Boolean)
      : []
  )
  const companies = taContactNames(ctx.contactNames, [
    ...new Set([...(a.maintenanceCompanyContactIds || []), ...planCompanyIds]),
  ])
  const staff = taContactNames(ctx.contactNames, a.maintenanceStaffContactIds)
  const hasPlanDetails = (a.maintenancePlans || []).some((plan) => plan.details)
  const assigned =
    !hasPlanDetails && a.assignedMaintenanceCompanyContactId
      ? taContactName(ctx.contactNames, a.assignedMaintenanceCompanyContactId)
      : ''
  const additionalIds = hasPlanDetails
    ? []
    : a.additionalMaintenanceCompanyContactIds &&
        a.additionalMaintenanceCompanyContactIds.length
      ? a.additionalMaintenanceCompanyContactIds
      : (a.typeFields && a.typeFields.additionalMaintenanceCompanyContactIds) ||
        []
  const additional = taContactNames(ctx.contactNames, additionalIds)
  const extraContacts = highlights
    .filter((h) => h.input === 'contact' || h.input === 'contactMulti')
    .map(
      (h) =>
        `${escapeHtml(h.label)}: ${escapeHtml(taHighlightValue(h, ctx.contactNames))}`
    )
  const parts = []
  if (companies.length) {
    const partners =
      companies.length > 1
        ? ` <span class="impowerx-ta-partners" title="${escapeHtml(companies.slice(1).join(', '))}">+${companies.length - 1} Partner</span>`
        : ''
    parts.push(
      `<div class="impowerx-ta-company">${TECHNICAL_ASSET_ROW_ICONS.building}<span class="impowerx-ta-strong impowerx-ta-company-name" title="${escapeHtml(companies[0])}">${escapeHtml(companies[0])}</span>${partners}</div>`
    )
  }
  if (assigned && companies.indexOf(assigned) === -1) {
    parts.push(taSecondary(`Zugewiesen: ${escapeHtml(assigned)}`))
  }
  if (additional.length) {
    parts.push(
      taSecondary(`Weitere Wartungsfirma: ${escapeHtml(additional.join(', '))}`)
    )
  }
  if (staff.length) parts.push(taAvatarStack(staff))
  if (extraContacts.length) parts.push(taSecondary(extraContacts.join('<br>')))
  return parts.length
    ? parts.join('')
    : '<span class="impowerx-ta-secondary">—</span>'
}

function renderTechnicalAssetDueCell(a, ctx) {
  const highlights =
    (ctx.highlightsByAssetId && ctx.highlightsByAssetId[String(a.id)]) || []
  const plan = taNextMaintenancePlan(a)
  const parts = []
  if (plan) {
    const cycle = taPlanCycleLabel(plan)
    parts.push(
      `<div class="impowerx-ta-strong">${isoDateToGermanDisplay(plan.nextMaintenanceDate)}${cycle ? ` (${escapeHtml(cycle)})` : ''}</div>`
    )
    if (plan.name) parts.push(taSecondary(escapeHtml(plan.name)))
  }
  const useLegacyContract = !(a.maintenancePlans || []).some(
    (item) => item.details
  )
  const contract =
    plan && plan.details && plan.details.contractEnabled
      ? plan.details
      : useLegacyContract
        ? a
        : {}
  const startValue = useLegacyContract
    ? taContractValue(a, highlights, 'contractStartDate')
    : contract.contractStartDate
  const endValue = useLegacyContract
    ? taContractValue(a, highlights, 'contractEndDate')
    : contract.contractEndDate
  if (startValue || endValue) {
    parts.push(
      taSecondary(
        `Laufzeit: ${startValue ? isoDateToGermanDisplay(startValue) : '…'} – ${endValue ? isoDateToGermanDisplay(endValue) : 'unbefristet'}`
      )
    )
  }
  const notice = useLegacyContract
    ? taContractValue(a, highlights, 'noticePeriodMonths')
    : contract.noticePeriodMonths
  if (notice != null && notice !== '') {
    parts.push(
      taSecondary(`Kündigungsfrist: ${escapeHtml(String(notice))} Monate`)
    )
  }
  if (contract.cancellationDate) {
    parts.push(
      taSecondary(
        `Kündigung: ${isoDateToGermanDisplay(contract.cancellationDate)}`
      )
    )
  }
  if (contract.contractStatus === 'stopped') {
    parts.push(taSecondary('Vertrag: Gestoppt'))
  }
  if (contract.contractStatus === 'cancelled') {
    parts.push(taSecondary('Vertrag: Gekündigt'))
  }
  highlights
    .filter(
      (h) => h.input === 'date' && !/^contract(Start|End)Date$/.test(h.key)
    )
    .forEach((h) =>
      parts.push(
        taSecondary(
          `${escapeHtml(h.label)}: ${isoDateToGermanDisplay(h.value)}`
        )
      )
    )
  return parts.length
    ? parts.join('')
    : `<span class="impowerx-ta-secondary">${(a.maintenancePlans || []).length ? 'Wartungspläne pausiert' : 'Kein Wartungsplan'}</span>`
}

function renderTechnicalAssetDocumentsCell(a, ctx) {
  const n = (a.documents || []).length
  if (!n) return '<span class="impowerx-ta-secondary">—</span>'
  return `<a class="impowerx-ta-doc-pill" href="${escapeHtml(ctx.detailHref(a))}" title="Dokumente öffnen">${TECHNICAL_ASSET_ROW_ICONS.file}${n} Dok.</a>`
}

function renderTechnicalAssetActionsCell(a, ctx, deleteAriaLabel) {
  return `<div class="impowerx-ta-row-actions">
    <a class="impowerx-ta-icon-btn impowerx-ta-edit-btn" href="${escapeHtml(ctx.detailHref(a))}" title="Bearbeiten" aria-label="Bearbeiten">${TECHNICAL_ASSET_ROW_ICONS.edit}</a>
    <button type="button" class="impowerx-ta-icon-btn impowerx-ta-delete-btn" title="Löschen" aria-label="${escapeHtml(deleteAriaLabel)}">${TECHNICAL_ASSET_ROW_ICONS.delete}</button>
  </div>`
}

/**
 * Summary bar under the Technische Anlagen table.
 * Upcoming text reads `maintenancePlans[].nextMaintenanceDate`
 * (`TechnicalAssetMaintenancePlans` on the list payload). Past dates are overdue,
 * not upcoming. This bar is rendered only after that payload arrives; while the
 * list is loading the table host shows the section's loading copy instead, so
 * the empty sentence is never shown before the plans are known.
 */
function renderTechnicalAssetsSummaryHtml(rows, buildings) {
  const buildingIds = new Set(rows.map((a) => String(a.buildingId)))
  const total =
    Array.isArray(buildings) && buildings.length
      ? buildings.length
      : buildingIds.size
  const dated = rows.flatMap((a) =>
    (a.maintenancePlans || [])
      .filter((p) => p && p.nextMaintenanceDate && taPlanIsActive(p, a))
      .map((plan) => ({
        a,
        plan,
        days: technicalAssetDaysUntil(plan.nextMaintenanceDate),
      }))
  )
  // Past dates are overdue, not "anstehend": count them instead of naming them as next.
  const overdueAssetIds = new Set(
    dated.filter((x) => x.days != null && x.days < 0).map((x) => String(x.a.id))
  )
  const next = dated
    .filter((x) => x.days != null && x.days >= 0)
    .sort((x, y) =>
      String(x.plan.nextMaintenanceDate).localeCompare(
        String(y.plan.nextMaintenanceDate)
      )
    )[0]
  const left = `${rows.length} ${rows.length === 1 ? 'Anlage' : 'Anlagen'} erfasst · ${buildingIds.size} von ${total} ${total === 1 ? 'Gebäude' : 'Gebäuden'} abgedeckt`
  const upcoming = next
    ? `Nächste anstehende Wartung: <b>${isoDateToGermanDisplay(next.plan.nextMaintenanceDate)}</b> (${escapeHtml(next.a.name || next.a.typ || '')})`
    : 'Keine anstehenden Wartungen geplant'
  const overdue = overdueAssetIds.size
    ? ` · <b>${overdueAssetIds.size}</b> ${overdueAssetIds.size === 1 ? 'Anlage' : 'Anlagen'} mit überfälliger Wartung`
    : ''
  const right = `${upcoming}${overdue}`
  return `<div class="impowerx-ta-summary"><span class="impowerx-ta-summary-left">${TECHNICAL_ASSET_ROW_ICONS.check}${escapeHtml(left)}</span><span>${right}</span></div>`
}

/** First Wartungsfirma shown in the company column (asset-level, then plan-level). */
function taPrimaryCompanyName(a, ctx) {
  const planCompanyIds = (a.maintenancePlans || []).flatMap((plan) =>
    plan.details
      ? [
          plan.details.assignedMaintenanceCompanyContactId,
          ...(plan.details.additionalMaintenanceCompanyContactIds || []),
        ].filter(Boolean)
      : []
  )
  const ids = [
    ...new Set([...(a.maintenanceCompanyContactIds || []), ...planCompanyIds]),
  ]
  return ids.length ? taContactName(ctx.contactNames, ids[0]) : null
}

/** Sort value for the Dokumente column. */
function taDocumentCount(a) {
  return (a.documents || []).length
}

/**
 * Rows carry `buildingLabel`; `ctx` carries `detailHref`, `contactNames`,
 * `highlightsByAssetId`, `buildings`, `view` and, when the rows are filtered,
 * `allRows` for the summary.
 */
function renderTechnicalAssetsTableHtml(rows, ctx) {
  return renderTechnicalAssetsTableShellHtml({
    columns: [
      {
        key: 'name',
        label: 'Name / Typ / Status',
        width: 23,
        cell: renderTechnicalAssetNameCell,
        sortValue: (a) => a.name,
      },
      {
        key: 'building',
        label: 'Gebäude',
        width: 16,
        cell: renderTechnicalAssetBuildingCell,
        sortValue: (a) => a.buildingLabel,
      },
      {
        key: 'company',
        label: 'Wartungsfirma & Ansprechpartner',
        width: 20,
        cell: renderTechnicalAssetCompanyCell,
        sortValue: taPrimaryCompanyName,
      },
      {
        key: 'due',
        label: 'Fälligkeit & Fristen',
        width: 20,
        cell: renderTechnicalAssetDueCell,
        sortValue: (a) => {
          const plan = taNextMaintenancePlan(a)
          return plan ? plan.nextMaintenanceDate : null
        },
      },
      {
        key: 'documents',
        label: 'Dokumente',
        width: 11,
        cell: renderTechnicalAssetDocumentsCell,
        sortValue: taDocumentCount,
      },
    ],
    rows,
    emptyText: 'Keine technischen Anlagen erfasst.',
    deleteAriaLabel: 'Technische Anlage löschen',
    summaryHtml: renderTechnicalAssetsSummaryHtml(
      ctx.allRows || rows,
      ctx.buildings
    ),
    ctx,
  })
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = { renderTechnicalAssetsSummaryHtml }
}
